import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { AuthenticatedUser, LoginInput, LoginResponse } from '@faktorplus/shared';
import { apiRequest, tokenStore } from '../lib/api-client';

interface AuthContextValue {
  user: AuthenticatedUser | null;
  isLoading: boolean;
  login: (input: LoginInput) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthenticatedUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const queryClient = useQueryClient();

  // Restore the session on load. Refresh tokens last 90 days on purpose:
  // a site manager must not be logged out of their phone between visits.
  useEffect(() => {
    let cancelled = false;

    async function restoreSession() {
      if (!tokenStore.read()) {
        setIsLoading(false);
        return;
      }

      try {
        const current = await apiRequest<AuthenticatedUser>('/auth/me');
        if (!cancelled) setUser(current);
      } catch {
        tokenStore.clear();
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    void restoreSession();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (input: LoginInput) => {
    const response = await apiRequest<LoginResponse>('/auth/login', {
      method: 'POST',
      body: input,
      skipRefresh: true,
    });

    tokenStore.write({
      accessToken: response.accessToken,
      refreshToken: response.refreshToken,
    });
    setUser(response.user);
  }, []);

  const logout = useCallback(async () => {
    const tokens = tokenStore.read();

    if (tokens?.refreshToken) {
      await apiRequest<void>('/auth/logout', {
        method: 'POST',
        body: { refreshToken: tokens.refreshToken },
        skipRefresh: true,
      }).catch(() => undefined);
    }

    tokenStore.clear();
    setUser(null);
    queryClient.clear();
  }, [queryClient]);

  const value = useMemo(
    () => ({ user, isLoading, login, logout }),
    [user, isLoading, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
}

/** The authenticated user, for screens that already sit behind a route guard. */
export function useCurrentUser(): AuthenticatedUser {
  const { user } = useAuth();
  if (!user) throw new Error('useCurrentUser used outside an authenticated route');
  return user;
}
